/**
 * analyticsService.js
 *
 * Thin wrapper around gtag for GA4 event tracking on the Start portal (/start/*).
 * All events include portal_surface: 'start' so they can be filtered in GA4 reports
 * and in the Lead Portal report page.
 *
 * Setup: see docs/GA4_LEAD_PORTAL_SETUP.md
 */

const MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID;

// Whether the gtag script has been injected into the DOM
let initialized = false;

/**
 * Inject the gtag.js script and configure the GA4 property.
 * Called once from AnalyticsTracker on first render.
 */
export function initGA4() {
  if (!MEASUREMENT_ID) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[Analytics] REACT_APP_GA4_MEASUREMENT_ID is not set — tracking disabled.');
    }
    return;
  }
  if (initialized || document.getElementById('ga4-script')) {
    initialized = true;
    return;
  }

  const script = document.createElement('script');
  script.id = 'ga4-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };

  window.gtag('js', new Date());
  // Disable automatic page_view — AnalyticsTracker fires them manually for SPA routing.
  window.gtag('config', MEASUREMENT_ID, {
    send_page_view: false,
    // Enable debug_mode only in local development
    ...(process.env.NODE_ENV === 'development' && { debug_mode: true }),
  });

  initialized = true;
}

/** Return true only when gtag is ready to accept calls. */
function isReady() {
  return initialized && MEASUREMENT_ID && typeof window.gtag === 'function';
}

// ---------------------------------------------------------------------------
// Identity
// ---------------------------------------------------------------------------

/**
 * Associate subsequent events with a specific Firebase UID.
 * Called when a Start portal user successfully authenticates.
 * Only the UID is sent — no name, email, or PII.
 */
export function setAnalyticsUser(uid, accountId) {
  if (!isReady()) return;
  window.gtag('set', 'user_id', uid);
  window.gtag('set', 'user_properties', {
    portal_surface: 'start',
    ...(accountId && { account_id: accountId }),
  });
}

/** Clear user identity on sign-out. */
export function clearAnalyticsUser() {
  if (!isReady()) return;
  window.gtag('set', 'user_id', null);
  window.gtag('set', 'user_properties', { portal_surface: null, account_id: null });
}

// ---------------------------------------------------------------------------
// Page views
// ---------------------------------------------------------------------------

/**
 * Send a manual page_view event.
 * Should be called on every route change within /start/*.
 */
export function trackPageView(pathname, title) {
  if (!isReady()) return;
  window.gtag('event', 'page_view', {
    page_path: pathname,
    page_title: title || document.title,
    portal_surface: 'start',
  });
}

// ---------------------------------------------------------------------------
// Generic event helper
// ---------------------------------------------------------------------------

/**
 * Send an arbitrary GA4 event.
 * Always appends portal_surface: 'start' so events are filterable.
 *
 * @param {string} eventName  - GA4 event name (snake_case)
 * @param {object} params     - Additional event parameters
 */
export function trackEvent(eventName, params = {}) {
  if (!isReady()) return;
  window.gtag('event', eventName, {
    portal_surface: 'start',
    ...params,
  });
}

// ---------------------------------------------------------------------------
// Typed helpers (preferred over calling trackEvent directly)
// ---------------------------------------------------------------------------

/**
 * User clicked a sidebar navigation item.
 * @param {string} tabName  - e.g. 'sales', 'operations'
 * @param {string} path     - destination path
 */
export function trackTabClick(tabName, path) {
  trackEvent('tab_click', { tab_name: tabName, page_path: path });
}

/**
 * User clicked a CTA or action button.
 * @param {string} buttonLabel - human-readable label on the button
 * @param {string} moduleName  - parent section, e.g. 'start_dashboard'
 */
export function trackButtonClick(buttonLabel, moduleName) {
  trackEvent('button_click', { button_label: buttonLabel, module_name: moduleName });
}

/**
 * A module or card became visible/interacted with.
 * @param {string} moduleName - e.g. 'roadmap_card', 'free_resource_banner'
 */
export function trackModuleView(moduleName) {
  trackEvent('module_view', { module_name: moduleName });
}

/**
 * User started playing a video.
 * @param {string} videoTitle - video title or identifier
 * @param {string} moduleName - parent section
 */
export function trackVideoPlay(videoTitle, moduleName) {
  trackEvent('video_start', { video_title: videoTitle, module_name: moduleName });
}

/**
 * User triggered a file download.
 * @param {string} fileName   - file name or URL
 * @param {string} fileType   - e.g. 'pdf', 'xlsx'
 * @param {string} moduleName - parent section
 */
export function trackFileDownload(fileName, fileType, moduleName) {
  trackEvent('file_download', { file_name: fileName, file_type: fileType, module_name: moduleName });
}

/**
 * User switched to a tab within a page (not sidebar nav).
 * @param {string} tabName   - tab label
 * @param {string} pageName  - parent page, e.g. 'start_sales'
 */
export function trackInPageTabClick(tabName, pageName) {
  trackEvent('in_page_tab_click', { tab_name: tabName, module_name: pageName });
}
